import React, { useMemo, useContext, useState, useEffect, useRef } from 'react';
import { WalkDataContext } from './hooks/walkDataContext';
import AudioEffectControls from './audioControllers/tonePlayer/AudioEffectControls';
import ButtonSpeedControls from './audioControllers/tonePlayer/ButtonSpeedControls';
import ButtonSpeedControlsAlt from './audioControllers/tonePlayer/ButtonSpeedControlsAlt';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGaugeHigh, faCircleLeft, faUndo} from '@fortawesome/free-solid-svg-icons';

// Feature Flags for Speed Control UI:
// - USE_BUTTON_SPEED_CONTROLS: true = Speedometer with buttons, false = slider
// - USE_ALT_BUTTON_CONTROLS: true = Compact buttons only (overrides USE_BUTTON_SPEED_CONTROLS)
const USE_BUTTON_SPEED_CONTROLS = true;
const USE_ALT_BUTTON_CONTROLS = true;

const AudioLab = ({ handleTrack }) => {
  const { tracks, currentTrack, storyMetas, language, updateAudioEffect, audioEffects, contentState } = useContext(WalkDataContext);
  const [currentTrackSlug, setCurrentTrackSlug] = useState(null);

  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches[0].pageY - containerRef.current.offsetTop);
    setScrollTop(containerRef.current.scrollTop);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const y = e.touches[0].pageY - containerRef.current.offsetTop;
    const walk = (y - startY) * 2;
    containerRef.current.scrollTop = scrollTop - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    setCurrentTrackSlug(currentTrack.slug);
  }, [currentTrack]);

  const renderedTracks = useMemo(() => {
    const activeTracks = contentState.filter(item => item.type === 'track' && item.state === 'active').map(item => item.slug);

    return tracks
      .filter(track => activeTracks.includes(track.slug) && !track.metadata.trackMeta_radio?.[1])
      .map((track) => {
        const isHighlighted = contentState.some(item =>
          item.type === 'track' &&
          item.slug === track.slug &&
          item.highlight === true
        );

        const trackName = track.metadata.trackMeta_name ? track.metadata.trackMeta_name[language] : 'Untitled Track';
        const isCurrentTrack = track.slug === currentTrackSlug;

        // Build accessible label
        let ariaLabel = trackName;
        if (isCurrentTrack) {
          ariaLabel += ', currently playing';
        }
        if (isHighlighted) {
          ariaLabel += ', new';
        }

        return (
          <button
            key={track.slug}
            onClick={() => handleTrack(track.slug)}
            className={`list-group-item list-group-item-action d-flex align-items-center audiolab-track-button ${isCurrentTrack ? 'active' : ''}`}
            aria-label={ariaLabel}
            aria-current={isCurrentTrack ? 'true' : undefined}
          >
            <div className="playlist-thumbnail-container" style={{ position: 'relative' }}>
              <div className="playlist-thumbnail">
                {(track.metadata.trackMeta_cover && track.metadata.trackMeta_cover[language]) ||
                (storyMetas.storyMeta_cover && storyMetas.storyMeta_cover[language]) ? (
                  <img
                    className="audio-image-small"
                    src={
                      (track.metadata.trackMeta_cover && track.metadata.trackMeta_cover[language]) ||
                      (storyMetas.storyMeta_cover && storyMetas.storyMeta_cover[language]) ||
                      "/assets/illustrations/placeholder-cover.jpg"
                    }
                    alt=""
                    aria-hidden="true"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/illustrations/placeholder-cover.jpg";
                    }}
                  />
                ) : (
                  <img
                    className="audio-image-small"
                    src="/assets/illustrations/placeholder-cover.jpg"
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </div>
              {isHighlighted && (
                <div
                  style={{
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'red',
                    zIndex: 1
                  }}
                  className="highlight-active-tracks"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="flex-grow-1" aria-hidden="true">
              {trackName}
            </div>
          </button>
        );
      });
  }, [tracks, currentTrackSlug, language, contentState, storyMetas, handleTrack]);

  //<AudioEffectControls effectName="reverbWet" label="reverb wet" type="slider" min={0} max={1} step={0.01} />
  //<AudioEffectControls effectName="reverbDecay" label="reverb decay" type="slider" min={0.001} max={10} step={0.01} />
  //<AudioEffectControls effectName="isPingPongDelayEnabled" label="Ping Pong Delay" type="toggle" />
  const resetAudioEffects = () =>{
    updateAudioEffect('playbackRate', 1);
    updateAudioEffect('isReversed', false);
  }

  return (
    <div
      ref={containerRef}
      className="content-with-fixed-controls"
      style={{
        overflow: 'auto',
        touchAction: 'none'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label="Audio lab track list with touch scrolling"
    >
      <div className='title-page title-audiolab'><h1>Audio Player</h1></div>
      <div className="playlist-wrapper list-group" role="list" aria-label="Available audio tracks">
        {renderedTracks}
      </div>

      <div
        className="audio-controls-fixed d-flex align-items-center gap-3 navbar-light bg-light audio-effects-section"
        role="region"
        aria-label="Audio playback controls"
      >
        {USE_ALT_BUTTON_CONTROLS ? (
          // Compact button-only speed controls (no speedometer)
          <ButtonSpeedControlsAlt
            className=""
          />
        ) : USE_BUTTON_SPEED_CONTROLS ? (
          // Speedometer with buttons
          <ButtonSpeedControls
            className=""
          />
        ) : (
          // Original slider-based speed control
          <>
            <Button
              className="mb-0 d-flex align-items-center justify-content-center ha-250 audio-effects-reset"
              disabled={audioEffects.playbackRate === 1 && !audioEffects.isReversed}
              onClick={() => resetAudioEffects()}
              aria-label="Reset audio effects to default"
            >
              <FontAwesomeIcon icon={faUndo} className="fs-4" aria-hidden="true" />
            </Button>

            <AudioEffectControls
              className="mb-0 ha-250 audio-effects-reverse"
              type="button"
              effectName="isReversed"
              activeIcon={faCircleLeft}
              inactiveIcon={faCircleLeft}
            />

            <div className="d-flex align-items-center gap-2 flex-grow-1 audio-effects-speed-section" role="group" aria-label="Playback speed control">
              <FontAwesomeIcon icon={faGaugeHigh} className="text-primary fs-4 flip-vertical audio-effects-speed-icon-left" aria-hidden="true" />
              <div className="flex-grow-1 audio-effects-speed-slider">
                <AudioEffectControls
                  effectName="playbackRate"
                  label=""
                  type="slider"
                  min={0.1}
                  max={5}
                  step={0.01}
                />
              </div>
              <FontAwesomeIcon icon={faGaugeHigh} className="text-primary fs-4 audio-effects-speed-icon-right" aria-hidden="true" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioLab;